import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbPaginationModule } from '@ng-bootstrap/ng-bootstrap';
import {
  buildDocPaginatorState,
  DocPaginatorState,
  formatDocPaginatorReport,
} from './doc-paginator.types';

@Component({
  selector: 'doc-paginator',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbPaginationModule],
  template: `
    <div class="doc-paginator-panel" [ngClass]="styleClass">
      @if (showCurrentPageReport) {
        <span class="doc-paginator-current">{{ currentPageReportText }}</span>
      }

      <div class="doc-paginator-controls">
        @if (rowsPerPageOptions?.length) {
          <div class="doc-paginator-rpp-options">
            <select
              class="form-select form-select-sm doc-paginator-rpp-select"
              [ngModel]="rows"
              (ngModelChange)="onRowsPerPageChange($event)">
              @for (option of rowsPerPageOptions; track option) {
                <option [ngValue]="option">{{ option }}</option>
              }
            </select>
          </div>
        }

        <ngb-pagination
          class="doc-paginator-pages"
          [collectionSize]="totalRecords"
          [page]="currentPage"
          [pageSize]="effectiveRows"
          [maxSize]="pageLinkSize"
          [rotate]="true"
          [boundaryLinks]="showFirstLastIcon"
          [disabled]="totalRecords === 0"
          (pageChange)="onPageChangeInternal($event)">
        </ngb-pagination>
      </div>
    </div>
  `,
  styleUrl: './doc-paginator.component.scss',
})
export class DocPaginatorComponent {
  @Input() rows = 10;
  @Input() first = 0;
  @Input() totalRecords = 0;
  @Input() rowsPerPageOptions: number[] | null = null;
  @Input() showFirstLastIcon = true;
  @Input() showCurrentPageReport = false;
  @Input() currentPageReportTemplate = '{first} - {last} of {totalRecords}';
  @Input() pageLinkSize = 5;
  @Input() styleClass = '';

  @Output() onPageChange = new EventEmitter<DocPaginatorState>();

  @HostBinding('class.doc-paginator-host')
  readonly hostClass = true;

  get effectiveRows(): number {
    return Math.max(1, this.rows);
  }

  get currentPage(): number {
    return Math.floor(this.first / this.effectiveRows) + 1;
  }

  get currentPageReportText(): string {
    return formatDocPaginatorReport(
      this.currentPageReportTemplate,
      this.first,
      this.effectiveRows,
      this.totalRecords
    );
  }

  onPageChangeInternal(page: number): void {
    const first = (page - 1) * this.effectiveRows;
    this.emitPageChange(first, this.effectiveRows);
  }

  onRowsPerPageChange(rows: number): void {
    const nextRows = Math.max(1, Number(rows));
    this.emitPageChange(0, nextRows);
  }

  private emitPageChange(first: number, rows: number): void {
    this.onPageChange.emit(buildDocPaginatorState(first, rows, this.totalRecords));
  }
}
